import React from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, FileText, RefreshCw, Send, Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { emailAPI, archiveAPI } from '../../services/api';

const EmailTab = ({
  emailRecipientType,
  setEmailRecipientType,
  emailSubject,
  setEmailSubject,
  emailMessage,
  setEmailMessage,
  emailRecipients,
  setEmailRecipients,
  selectedEmails,
  setSelectedEmails,
  sendingEmail,
  setSendingEmail,
  emailLogs,
  setEmailLogs,
  bulkSelectMode,
  setBulkSelectMode,
  selectedEmailIds,
  setSelectedEmailIds,
  handleBulkDeleteEmails
}) => {
  const toggleEmailSelection = (emailId) => {
    setSelectedEmailIds(prev => 
      prev.includes(emailId) 
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    setSendingEmail(true);
    try {
      const data = {
        recipient_type: emailRecipientType,
        subject: emailSubject,
        message: emailMessage,
        recipient_emails: emailRecipientType === 'specific' ? selectedEmails : undefined
      };
      
      const response = await emailAPI.sendEmail(data);
      toast.success(response.data.message);
      setEmailSubject('');
      setEmailMessage('');
      setSelectedEmails([]);
      
      // Refresh email logs
      const logsRes = await emailAPI.getEmailLogs();
      setEmailLogs(logsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleRecipientTypeChange = async (value) => {
    setEmailRecipientType(value);
    setSelectedEmails([]);
    if (value === 'specific') {
      try {
        const [workersRes, customersRes] = await Promise.all([
          emailAPI.getRecipients('workers'),
          emailAPI.getRecipients('customers')
        ]);
        setEmailRecipients([
          ...workersRes.data.map(w => ({...w, type: 'worker'})),
          ...customersRes.data.map(c => ({...c, type: 'customer'}))
        ]);
      } catch (error) {
        console.error('Failed to fetch recipients', error);
      }
    }
  };

  const refreshEmailLogs = async () => {
    try {
      const res = await emailAPI.getEmailLogs();
      setEmailLogs(res.data);
    } catch (e) {
      console.error('Failed to fetch email logs');
    }
  };

  const deleteEmailLog = async (logId) => {
    if (window.confirm('Delete this email log?')) {
      try {
        await archiveAPI.bulkDeleteEmails([logId]);
        toast.success('Email deleted');
        refreshEmailLogs();
      } catch (err) {
        toast.error('Failed to delete email');
      }
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="email-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Send Email</h1>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email Compose Form */}
        <div className="lg:col-span-2">
          <div className="card p-6">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[var(--primary)]" />
              Compose Email
            </h2>
            
            <form onSubmit={handleSendEmail} className="space-y-4">
              {/* Recipient Type */}
              <div>
                <Label className="font-semibold">Send To</Label>
                <select 
                  value={emailRecipientType}
                  onChange={(e) => handleRecipientTypeChange(e.target.value)}
                  className="w-full mt-1 p-2 border border-[var(--border-primary)] rounded-lg bg-[var(--background-subtle)] text-[var(--text-primary)]"
                >
                  <option value="all_workers">All Workers</option>
                  <option value="all_customers">All Customers</option>
                  <option value="all">All Workers & Customers</option>
                  <option value="specific">Select Specific People</option>
                </select>
              </div>
              
              {/* Specific Recipients Selection */}
              {emailRecipientType === 'specific' && (
                <div>
                  <Label className="font-semibold">Select Recipients</Label>
                  <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {emailRecipients.length === 0 ? (
                      <p className="text-sm text-[var(--text-muted)] p-2">Loading recipients...</p>
                    ) : (
                      emailRecipients.map((recipient) => (
                        <label 
                          key={recipient.id} 
                          className="flex items-center gap-2 p-2 hover:bg-[var(--background-subtle)] rounded cursor-pointer"
                        >
                          <input 
                            type="checkbox"
                            checked={selectedEmails.includes(recipient.email)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedEmails([...selectedEmails, recipient.email]);
                              } else {
                                setSelectedEmails(selectedEmails.filter(em => em !== recipient.email));
                              }
                            }}
                          />
                          <span className="flex-1">{recipient.full_name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            recipient.type === 'worker' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-[var(--success)]/15 text-green-700'
                          }`}>
                            {recipient.type}
                          </span>
                          <span className="text-sm text-[var(--text-muted)]">{recipient.email}</span>
                        </label>
                      ))
                    )}
                  </div>
                  {selectedEmails.length > 0 && (
                    <p className="text-sm text-[var(--primary)] mt-1">{selectedEmails.length} selected</p>
                  )}
                </div>
              )}
              
              {/* Subject */}
              <div>
                <Label className="font-semibold">Subject</Label>
                <Input 
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  className="mt-1"
                  required
                />
              </div>
              
              {/* Message */}
              <div>
                <Label className="font-semibold">Message</Label>
                <Textarea 
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Write your message here..."
                  className="mt-1 min-h-[200px]"
                  required
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={sendingEmail || (emailRecipientType === 'specific' && selectedEmails.length === 0)}
              >
                {sendingEmail ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
        
        {/* Email History */}
        <div className="lg:col-span-1">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-[var(--secondary)]" />
                Recent Emails
              </h2>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={refreshEmailLogs}
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {emailLogs.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)] text-center py-4">No emails sent yet</p>
              ) : (
                <>
                  {/* Bulk Select Toggle for Emails */}
                  <div className="flex items-center justify-between mb-3">
                    <Button 
                      variant={bulkSelectMode.emails ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setBulkSelectMode(prev => ({ ...prev, emails: !prev.emails }));
                        setSelectedEmailIds([]);
                      }}
                      className={bulkSelectMode.emails ? "bg-rose-600 text-white" : ""}
                    >
                      {bulkSelectMode.emails ? "Cancel" : "Select Multiple"}
                    </Button>
                    {bulkSelectMode.emails && selectedEmailIds.length > 0 && (
                      <Button 
                        onClick={handleBulkDeleteEmails}
                        size="sm"
                        className="bg-[var(--error)] hover:bg-[var(--error)]/80 text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete ({selectedEmailIds.length})
                      </Button>
                    )}
                  </div>
                  {emailLogs.map((log) => (
                    <div key={log.id} className={`p-3 bg-[var(--background-subtle)] rounded-lg relative ${bulkSelectMode.emails && selectedEmailIds.includes(log.id) ? 'ring-2 ring-rose-500' : ''}`}>
                      {bulkSelectMode.emails && (
                        <div className="absolute top-2 right-2">
                          <Checkbox
                            checked={selectedEmailIds.includes(log.id)}
                            onCheckedChange={() => toggleEmailSelection(log.id)}
                            className="w-4 h-4"
                          />
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm truncate flex-1 pr-6">{log.subject}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          log.recipient_type === 'all_workers' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                          log.recipient_type === 'all_customers' ? 'bg-[var(--success)]/15 text-green-700' :
                          log.recipient_type === 'all' ? 'bg-[#F4C2C2]/15 text-purple-700' :
                          'bg-[var(--background-subtle)] text-[var(--text-primary)]'
                        }`}>
                          {log.recipient_type.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-[var(--text-muted)]">
                          Sent to {log.recipient_count} • {new Date(log.sent_at).toLocaleDateString()}
                        </p>
                        {!bulkSelectMode.emails && (
                          <button
                            onClick={() => deleteEmailLog(log.id)}
                            className="text-[var(--error)] hover:text-red-700 p-1"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default EmailTab;
